import { chatApi } from '@/store/api/conversation/chat/chat.api';
import { removeChatChannel, upsertChatChannel } from '@/store/api/conversation/chat/chat.group.cache';
import type { TypeAppDispatch } from '@/store/store';
import type { TypeChatChannelUpsert, TypeChatMember, TypeChatMemberRole } from '@/types/conversation/types.conversation.chat.channel';

export type TypeChannelOnly = TypeChatChannelUpsert['channel'];

const writeBack = (dispatch: TypeAppDispatch, queryFulfilled: Promise<{ data: TypeChannelOnly }>) => queryFulfilled
    .then(({ data }) => upsertChatChannel(dispatch, data))
    .catch(() => undefined);

const dropBack = (dispatch: TypeAppDispatch, queryFulfilled: Promise<{ data: { id: string } }>) => queryFulfilled
    .then(({ data }) => removeChatChannel(dispatch, data.id))
    .catch(() => undefined);

const groupUrl = (id: string, rest = '') => `/conversations/chat/groups/${encodeURIComponent(id)}${rest}`;

export const chatGroupApi = chatApi.injectEndpoints({
    endpoints: (build) => ({
        listChatPeople: build.query<TypeChatMember[], void>({
            query: () => ({ url: '/conversations/chat/people' }),
            keepUnusedDataFor: 120,
        }),

        createChatGroup: build.mutation<TypeChannelOnly, { name: string; topic: string; memberIds: string[] }>({
            query: (data) => ({ url: '/conversations/chat/groups', method: 'post', data }),
            onQueryStarted: (_arg, { dispatch, queryFulfilled }) => { void writeBack(dispatch, queryFulfilled); },
        }),

        openChatDirect: build.mutation<TypeChannelOnly, { peerId: string }>({
            query: (data) => ({ url: '/conversations/chat/direct', method: 'post', data }),
            onQueryStarted: (_arg, { dispatch, queryFulfilled }) => { void writeBack(dispatch, queryFulfilled); },
        }),

        updateChatGroup: build.mutation<TypeChannelOnly, { id: string; name: string; topic: string }>({
            query: ({ id, name, topic }) => ({ url: groupUrl(id), method: 'patch', data: { name, topic } }),
            onQueryStarted: (_arg, { dispatch, queryFulfilled }) => { void writeBack(dispatch, queryFulfilled); },
        }),

        setChatEphemeral: build.mutation<TypeChannelOnly, { id: string; seconds: number }>({
            query: ({ id, seconds }) => ({ url: `/conversations/chat/channels/${encodeURIComponent(id)}/ephemeral`, method: 'post', data: { seconds } }),
            onQueryStarted: (_arg, { dispatch, queryFulfilled }) => { void writeBack(dispatch, queryFulfilled); },
        }),

        addChatGroupMembers: build.mutation<TypeChannelOnly, { id: string; userIds: string[] }>({
            query: ({ id, userIds }) => ({ url: groupUrl(id, '/members'), method: 'post', data: { userIds } }),
            onQueryStarted: (_arg, { dispatch, queryFulfilled }) => { void writeBack(dispatch, queryFulfilled); },
        }),

        removeChatGroupMember: build.mutation<TypeChannelOnly, { id: string; userId: string }>({
            query: ({ id, userId }) => ({ url: groupUrl(id, `/members/${encodeURIComponent(userId)}`), method: 'delete' }),
            onQueryStarted: (_arg, { dispatch, queryFulfilled }) => { void writeBack(dispatch, queryFulfilled); },
        }),

        setChatGroupRole: build.mutation<TypeChannelOnly, { id: string; userId: string; role: TypeChatMemberRole }>({
            query: ({ id, userId, role }) => ({ url: groupUrl(id, `/members/${encodeURIComponent(userId)}`), method: 'patch', data: { role } }),
            onQueryStarted: (_arg, { dispatch, queryFulfilled }) => { void writeBack(dispatch, queryFulfilled); },
        }),

        leaveChatGroup: build.mutation<{ id: string }, string>({
            query: (id) => ({ url: groupUrl(id, '/leave'), method: 'post' }),
            onQueryStarted: (_arg, { dispatch, queryFulfilled }) => { void dropBack(dispatch, queryFulfilled); },
        }),

        removeChatGroup: build.mutation<{ id: string }, string>({
            query: (id) => ({ url: groupUrl(id), method: 'delete' }),
            onQueryStarted: (_arg, { dispatch, queryFulfilled }) => { void dropBack(dispatch, queryFulfilled); },
        }),
    }),
});

export const {
    useListChatPeopleQuery, useCreateChatGroupMutation, useOpenChatDirectMutation, useUpdateChatGroupMutation,
    useAddChatGroupMembersMutation, useRemoveChatGroupMemberMutation, useSetChatGroupRoleMutation,
    useLeaveChatGroupMutation, useRemoveChatGroupMutation, useSetChatEphemeralMutation,
} = chatGroupApi;

export default chatGroupApi;
